import type { CSSProperties } from "react";
import type { DayTheme } from "../../shared/event";

export type ThemeKey = DayTheme | "brand";

export interface Palette {
  bg: string;
  soft: string;
  main: string;
  deep: string;
}

// Keep in sync with tailwind.config.ts colors.
export const THEMES: Record<ThemeKey, Palette> = {
  brand: { bg: "#eef2f9", soft: "#dde6f3", main: "#5b8fd6", deep: "#2f5fa6" }, // blue/silver
  day1: { bg: "#fdf0f4", soft: "#f9dce6", main: "#ef8fb0", deep: "#c75e84" }, // pink
  day2: { bg: "#eef3fb", soft: "#d9e6f7", main: "#6aa0e0", deep: "#3568b5" }, // blue
  day3: { bg: "#eef6f0", soft: "#d6ecdb", main: "#84c98f", deep: "#4a9b59" }, // green
};

/** CSS custom properties consumed by .btn-primary / .chip / .field etc. */
export function paletteStyle(p: Palette): CSSProperties {
  return {
    "--theme-bg": p.bg,
    "--theme-soft": p.soft,
    "--theme-main": p.main,
    "--theme-deep": p.deep,
  } as CSSProperties;
}

export function themeStyle(theme: ThemeKey): CSSProperties {
  return paletteStyle(THEMES[theme]);
}

/** Badge image per day (optimized files placed in /public/img). */
export const DAY_IMAGE: Record<DayTheme, string> = {
  day1: "/img/badge-day1.jpg",
  day2: "/img/badge-day2.jpg",
  day3: "/img/badge-day3.jpg",
};

/**
 * Experimental: 10 round HBD images, each with a curated palette matching its colour.
 * On load the site randomly picks one as the hero; clicking a thumbnail re-themes the page.
 */
export interface HbdImageTheme extends Palette {
  img: string;
}

export const HBD_IMAGE_THEMES: HbdImageTheme[] = [
  { img: "/img/badge-day1.jpg", bg: "#fdf0f4", soft: "#f9dce6", main: "#ef8fb0", deep: "#c75e84" }, // coral pink
  { img: "/img/badge-day2.jpg", bg: "#eef3fb", soft: "#d9e6f7", main: "#6aa0e0", deep: "#3568b5" }, // blue
  { img: "/img/badge-day3.jpg", bg: "#eef6f0", soft: "#d6ecdb", main: "#84c98f", deep: "#4a9b59" }, // grass green
  { img: "/img/badge-day4.jpg", bg: "#eef7f2", soft: "#d3edde", main: "#79c6a4", deep: "#3d977a" }, // sage mint
  { img: "/img/badge-day5.jpg", bg: "#ebf6f9", soft: "#cce9f0", main: "#4fb8cc", deep: "#1f7e93" }, // teal
  { img: "/img/badge-day6.jpg", bg: "#f6f1ea", soft: "#e7d9c6", main: "#c39a68", deep: "#875733" }, // mocha brown
  { img: "/img/badge-day7.jpg", bg: "#f1f4e9", soft: "#dfe5c9", main: "#9fb06a", deep: "#5e6d33" }, // olive
  { img: "/img/badge-day8.jpg", bg: "#f4f0fb", soft: "#e4d8f4", main: "#a98bd9", deep: "#6b46b3" }, // lavender
  { img: "/img/badge-day9.jpg", bg: "#fdf3ec", soft: "#fadfc8", main: "#f0a878", deep: "#c5703c" }, // peach
  { img: "/img/badge-day10.jpg", bg: "#fdf8e6", soft: "#f7eabb", main: "#edc645", deep: "#9c7613" }, // gold yellow
];
